/**
 * The client's copy of what the signed-in user may do.
 *
 * **This is not a security boundary, and it is worth being blunt about that.**
 * Everything here decides what to *show*. The server re-authorises every
 * mutation regardless, and a user who edits this in a debugger gains a broken
 * interface and nothing else. If a check here were the only thing standing
 * between someone and an action, the backend would be wrong, not this file.
 *
 * What it must do is *agree* with the server. The matching rule below is a
 * deliberate port of `AccessSnapshot.can` in `hardauto.modules.access.domain`,
 * which is itself a port of the Flutter client's. Three implementations, one
 * rule — and when they disagree, the symptom is a button that appears and then
 * fails, which is the least debuggable authorisation bug there is.
 */

/** What `GET /me/permissions` returns. */
export interface PermissionsPayload {
  version?: string | null;
  canAll?: boolean;
  roles?: string[];
  abilities?: string[];
  rules?: AccessRule[];
}

/** A CASL-shaped attribute rule. */
export interface AccessRule {
  action: string;
  subject?: string;
  conditions?: Record<string, unknown>;
  fields?: string[];
  inverted?: boolean;
  /** Conditions withheld; resolve through `POST /access/check`. */
  serverOnly?: boolean;
}

const WILDCARD = "*";

/**
 * A principal's permissions at one instant.
 *
 * Immutable on purpose: it is replaced when the server sends a new one, never
 * mutated. A snapshot that changed under a render would make two checks in the
 * same paint disagree.
 */
export class AccessSnapshot {
  readonly canAll: boolean;
  readonly roles: ReadonlySet<string>;
  readonly grants: ReadonlySet<string>;
  readonly rules: readonly AccessRule[];
  readonly version: string | null;

  private constructor(payload: PermissionsPayload) {
    this.canAll = payload.canAll ?? false;
    this.roles = new Set(payload.roles ?? []);
    this.grants = new Set(payload.abilities ?? []);
    this.rules = payload.rules ?? [];
    this.version = payload.version ?? null;
  }

  static from(payload: PermissionsPayload): AccessSnapshot {
    return new AccessSnapshot(payload);
  }

  /** Grants nothing. The fail-closed default before permissions have loaded. */
  static empty(): AccessSnapshot {
    return new AccessSnapshot({});
  }

  /**
   * Whether this principal holds `key`.
   *
   * Walks the key right to left, so `users.*` satisfies `users.profile.update`
   * and `*` satisfies everything. Character-for-character the same rule as the
   * server's; the shared test cases live in `__tests__/snapshot.test.ts` and
   * mirror the backend's own.
   */
  can(key: string): boolean {
    if (this.canAll) return true;
    if (this.grants.has(key)) return true;

    let dot = key.lastIndexOf(".");
    while (dot !== -1) {
      if (this.grants.has(`${key.slice(0, dot)}.${WILDCARD}`)) return true;
      dot = key.lastIndexOf(".", dot - 1);
    }

    return this.grants.has(WILDCARD);
  }

  /** Whether the principal holds every one of `keys`. */
  canAllOf(keys: readonly string[]): boolean {
    return keys.every((key) => this.can(key));
  }

  /** Whether the principal holds at least one of `keys`. */
  canAnyOf(keys: readonly string[]): boolean {
    return keys.some((key) => this.can(key));
  }

  /**
   * Whether an attribute rule for `action` needs the server to resolve it.
   *
   * A `serverOnly` rule arrived without its conditions, deliberately — they
   * would themselves reveal something. A caller seeing `true` here must ask
   * `POST /access/check` rather than guess, and guessing "allowed" would show
   * a control the server will refuse.
   */
  requiresServerCheck(action: string): boolean {
    return this.rules.some((rule) => rule.action === action && rule.serverOnly === true);
  }
}
